/**
 * Client-safe price catalog types and checks.
 * SQLite reads live in catalog.ts so this module can be imported from the browser.
 */

import {
  PLAN_TIERS,
  checkoutPlanId,
  type BillingInterval,
  type CheckoutPlanId,
  type PlanTier,
  type PlanTierDefinition,
} from "@/lib/subscription/planCatalog";

export const CHECKOUT_PLAN_IDS = [
  "starter-monthly",
  "starter-yearly",
  "professional-monthly",
  "professional-yearly",
] as const satisfies readonly CheckoutPlanId[];

export type PricedPlanId = (typeof CHECKOUT_PLAN_IDS)[number];

export const PRICE_ENV_KEYS: Record<PricedPlanId, string> = {
  "starter-monthly": "AIRWALLEX_PRICE_STARTER_MONTHLY",
  "starter-yearly": "AIRWALLEX_PRICE_STARTER_YEARLY",
  "professional-monthly": "AIRWALLEX_PRICE_PROFESSIONAL_MONTHLY",
  "professional-yearly": "AIRWALLEX_PRICE_PROFESSIONAL_YEARLY",
};

export const PLAN_PRICE_LABELS: Record<PricedPlanId, string> = {
  "starter-monthly": "Basic · Monthly",
  "starter-yearly": "Basic · Yearly",
  "professional-monthly": "Pro Research · Monthly",
  "professional-yearly": "Pro Research · Yearly",
};

export const HKD_AMOUNT_MAX = 1_000_000;
export const PROMO_TEXT_MAX = 40;

export type PublicPlanPrice = {
  amountHkd: number;
  compareAtHkd: number | null;
};

export type PublicPriceOffer = {
  applicationId: string;
  promotionEnabled: boolean;
  promoTextEn: string;
  promoTextZhCn: string;
  promoTextZhHk: string;
  plans: Record<PricedPlanId, PublicPlanPrice>;
};

export type PlanPriceInput = {
  planId: PricedPlanId;
  amountHkd: number;
  compareAtHkd: number | null;
};

export type AdminPlanField = {
  planId: PricedPlanId;
  amountHkd: number | null;
  compareAtHkd: number | null;
  airwallexPriceId: string | null;
  airwallexProductId: string | null;
};

export type AdminPriceOffer = {
  applicationId: string;
  label: string;
  isDefaultProduct: boolean;
  saved: boolean;
  promotionEnabled: boolean;
  promoTextEn: string;
  promoTextZhCn: string;
  promoTextZhHk: string;
  updatedAt: string | null;
  plans: AdminPlanField[];
};

export type PriceOfferInput = {
  applicationId: string;
  promotionEnabled: boolean;
  promoTextEn: string;
  promoTextZhCn: string;
  promoTextZhHk: string;
  plans: PlanPriceInput[];
};

export function isPricedPlanId(value: string): value is PricedPlanId {
  return (CHECKOUT_PLAN_IDS as readonly string[]).includes(value);
}

export function defaultPlanAmounts(): Record<PricedPlanId, number> {
  const out = {} as Record<PricedPlanId, number>;
  for (const tier of PLAN_TIERS) {
    if (tier.monthlyAmountHkd == null || tier.yearlyAmountHkd == null) continue;
    const monthly = checkoutPlanId(tier.id, "monthly");
    const yearly = checkoutPlanId(tier.id, "yearly");
    if (monthly && isPricedPlanId(monthly)) out[monthly] = tier.monthlyAmountHkd;
    if (yearly && isPricedPlanId(yearly)) out[yearly] = tier.yearlyAmountHkd;
  }
  return out;
}

export function defaultPublicPriceOffer(applicationId: string): PublicPriceOffer {
  const amounts = defaultPlanAmounts();
  const plans = {} as Record<PricedPlanId, PublicPlanPrice>;
  for (const id of CHECKOUT_PLAN_IDS) {
    plans[id] = { amountHkd: amounts[id], compareAtHkd: null };
  }
  return {
    applicationId,
    promotionEnabled: false,
    promoTextEn: "",
    promoTextZhCn: "",
    promoTextZhHk: "",
    plans,
  };
}

export function tierWithOffer(
  tier: PlanTierDefinition,
  offer: PublicPriceOffer
): PlanTierDefinition {
  if (tier.monthlyAmountHkd == null || tier.yearlyAmountHkd == null) return tier;
  const monthly = checkoutPlanId(tier.id, "monthly");
  const yearly = checkoutPlanId(tier.id, "yearly");
  if (!monthly || !yearly || !isPricedPlanId(monthly) || !isPricedPlanId(yearly)) {
    return tier;
  }
  return {
    ...tier,
    monthlyAmountHkd: offer.plans[monthly].amountHkd,
    yearlyAmountHkd: offer.plans[yearly].amountHkd,
  };
}

export function promoTextForLocale(offer: PublicPriceOffer, locale: string): string {
  if (locale === "zh-CN") {
    return offer.promoTextZhCn.trim() || offer.promoTextEn.trim();
  }
  if (locale === "zh-HK") {
    return offer.promoTextZhHk.trim() || offer.promoTextEn.trim();
  }
  return offer.promoTextEn.trim();
}

/** Strikethrough previous price and badge, when this plan is on promotion. */
export function promotionForPlan(
  offer: PublicPriceOffer,
  tierId: PlanTier,
  interval: BillingInterval,
  locale: string
): { compareAtHkd: number; text: string } | null {
  if (!offer.promotionEnabled || tierId === "enterprise") return null;
  const planId = checkoutPlanId(tierId, interval);
  if (!planId || !isPricedPlanId(planId)) return null;
  const slot = offer.plans[planId];
  if (slot.compareAtHkd == null || slot.compareAtHkd <= slot.amountHkd) return null;
  const text = promoTextForLocale(offer, locale);
  if (!text) return null;
  return { compareAtHkd: slot.compareAtHkd, text };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseHkdAmount(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isInteger(value)) return null;
  if (value < 1 || value > HKD_AMOUNT_MAX) return null;
  return value;
}

function parsePromoText(
  value: unknown,
  label: string,
  required: boolean
): { ok: true; text: string } | { ok: false; error: string } {
  if (typeof value !== "string") {
    return { ok: false, error: `${label} must be text.` };
  }
  const text = value.trim();
  if (!text) {
    if (required) return { ok: false, error: `${label} is required.` };
    return { ok: true, text: "" };
  }
  if (text.length > PROMO_TEXT_MAX) {
    return {
      ok: false,
      error: `${label} must be ${PROMO_TEXT_MAX} characters or fewer.`,
    };
  }
  if (/[<>]/.test(text) || /[\r\n]/.test(text)) {
    return { ok: false, error: `${label} must be plain text.` };
  }
  return { ok: true, text };
}

export function parsePriceOfferInput(
  body: unknown
): { ok: true; value: PriceOfferInput } | { ok: false; error: string } {
  if (!isRecord(body)) {
    return { ok: false, error: "Invalid request body." };
  }

  const applicationId =
    typeof body.applicationId === "string" ? body.applicationId.trim() : "";
  if (!applicationId) {
    return { ok: false, error: "applicationId is required." };
  }

  if (typeof body.promotionEnabled !== "boolean") {
    return { ok: false, error: "promotionEnabled must be true or false." };
  }
  const promotionEnabled = body.promotionEnabled;

  const en = parsePromoText(body.promoTextEn, "English badge", promotionEnabled);
  if (!en.ok) return en;
  const zhCn = parsePromoText(
    body.promoTextZhCn,
    "Simplified Chinese badge",
    promotionEnabled
  );
  if (!zhCn.ok) return zhCn;
  const zhHk = parsePromoText(
    body.promoTextZhHk,
    "Traditional Chinese badge",
    promotionEnabled
  );
  if (!zhHk.ok) return zhHk;

  if (!Array.isArray(body.plans)) {
    return { ok: false, error: "plans must be a list." };
  }

  const seen = new Set<string>();
  const plans: PlanPriceInput[] = [];
  for (const raw of body.plans) {
    if (!isRecord(raw) || typeof raw.planId !== "string" || !isPricedPlanId(raw.planId)) {
      return { ok: false, error: "Each plan needs a known planId." };
    }
    if (seen.has(raw.planId)) {
      return { ok: false, error: `Duplicate plan ${raw.planId}.` };
    }
    seen.add(raw.planId);
    const amountHkd = parseHkdAmount(raw.amountHkd);
    if (amountHkd == null) {
      return {
        ok: false,
        error: `${PLAN_PRICE_LABELS[raw.planId]} needs a whole HKD amount from 1 to ${HKD_AMOUNT_MAX.toLocaleString("en-HK")}.`,
      };
    }

    let compareAtHkd: number | null = null;
    if (raw.compareAtHkd != null) {
      compareAtHkd = parseHkdAmount(raw.compareAtHkd);
      if (compareAtHkd == null || compareAtHkd <= amountHkd) {
        return {
          ok: false,
          error: `${PLAN_PRICE_LABELS[raw.planId]} previous price must be a whole HKD amount greater than the new price.`,
        };
      }
    } else if (promotionEnabled) {
      return {
        ok: false,
        error: `${PLAN_PRICE_LABELS[raw.planId]} needs a previous price while the promotion is on.`,
      };
    }

    plans.push({ planId: raw.planId, amountHkd, compareAtHkd });
  }

  if (plans.length !== CHECKOUT_PLAN_IDS.length) {
    return {
      ok: false,
      error: "Basic and Pro Research each need a monthly and a yearly price.",
    };
  }

  return {
    ok: true,
    value: {
      applicationId,
      promotionEnabled,
      promoTextEn: en.text,
      promoTextZhCn: zhCn.text,
      promoTextZhHk: zhHk.text,
      plans,
    },
  };
}
